import {ZoomIn, ZoomOut} from "assets/icons";
import classNames from "classnames";
import {FC, useMemo, useState} from "react";
import {ComposableMap, Geographies, Geography, Marker, ZoomableGroup} from "react-simple-maps";
import {IWorldMapProps, TWorldMapPosition} from "./types";
import {
	getUSStateColor,
	getUSStateHoverColor,
	getUSStateRegion,
	isViewingUS,
	US_REGION_LABELS,
} from "./usRegions";
import {defaultGeographyStyle} from "./utils";
import styles from "./WorldMap.module.scss";

const REGION_CONFIG: Record<string, {label: string; coordinates: [number, number]}[]> = {
	"North America": [{label: "North America", coordinates: [-100, 52]}],
	Europe: [{label: "Europe", coordinates: [15, 52]}],
	"Asia Pacific": [
		{label: "Asia", coordinates: [90, 35]},
		{label: "Australia", coordinates: [135, -25]},
		{label: "Oceania", coordinates: [160, -20]},
	],
	"Central and South America": [
		{label: "Central America", coordinates: [-90, 15]},
		{label: "South America", coordinates: [-65, -20]},
	],
	Africa: [{label: "Africa", coordinates: [20, 10]}],
	"Middle East": [{label: "Middle East", coordinates: [45, 25]}],
};

const OCEAN_CONFIG: {label: string; coordinates: [number, number]}[] = [
	{label: "Atlantic Ocean", coordinates: [-30, 10]},
	{label: "Pacific Ocean", coordinates: [170, 0]},
	{label: "Pacific Ocean", coordinates: [-160, 0]},
	{label: "Indian Ocean", coordinates: [80, -10]},
];

const WorldMap: FC<IWorldMapProps> = ({
	width = 800,
	height = 640,
	className,
	geographyPath = "/features.json",
	markedCountries = [],
	getCountryColor,
	geographyStyle = defaultGeographyStyle,
	onGeographyHover,
	onGeographyLeave,
	onMouseMove,
	children,
	regionsByCountry = {},
	initialPosition = {coordinates: [0, 0], zoom: 1},
	onMoveEnd,
	showZoomControls = true,
}) => {
	const [position, setPosition] = useState<TWorldMapPosition>(initialPosition);

	const handleZoomIn = () => {
		setPosition((pos) => ({
			...pos,
			zoom: Math.min(pos.zoom * 1.5, 20),
		}));
	};

	const handleZoomOut = () => {
		setPosition((pos) => ({
			...pos,
			zoom: Math.max(pos.zoom / 1.5, 1),
		}));
	};

	const handleReset = () => {
		setPosition({coordinates: [0, 0], zoom: 1});
	};

	const handleMoveEnd = (newPosition: {coordinates: [number, number]; zoom: number}) => {
		setPosition(newPosition);
		onMoveEnd?.(newPosition);
	};

	const handleFilter = (event: unknown): boolean => {
		return (
			event instanceof MouseEvent ||
			event instanceof TouchEvent ||
			(typeof WheelEvent !== "undefined" && event instanceof WheelEvent)
		);
	};

	const mapRef = (ref: HTMLDivElement | null) => {
		if (ref) {
			ref.addEventListener(
				"wheel",
				(e) => {
					if (e.ctrlKey || e.metaKey || position.zoom > 1 || position.zoom < 20) {
						e.preventDefault();
					}
				},
				{passive: false},
			);
		}
	};

	// Check if we're focused on the US (for larger labels when zoomed)
	const isUSFocused = useMemo(() => {
		return isViewingUS(position.coordinates, position.zoom);
	}, [position.coordinates, position.zoom]);

	return (
		<div className={classNames(styles.mapWrapper, className)}>
			{showZoomControls && (
				<div className={styles.zoom}>
					<span>Zoom</span>
					{position.zoom < 20 && (
						<div onClick={handleZoomIn} title="Zoom In">
							<ZoomIn />
						</div>
					)}
					{position.zoom > 1 && (
						<div onClick={handleZoomOut} title="Zoom Out">
							<ZoomOut />
						</div>
					)}
					{(position.zoom !== 1 ||
						position.coordinates[0] !== 0 ||
						position.coordinates[1] !== 0) && (
						<div onClick={handleReset} className={styles.resetBtn} title="Reset View">
							Reset
						</div>
					)}
				</div>
			)}

			<div className={styles.mapContainer} ref={mapRef}>
				<ComposableMap
					projection={"geoMercator"}
					width={width}
					height={height}
					projectionConfig={{
						scale: 110,
						rotate: [-11, 0, 0],
					}}
					onMouseMove={onMouseMove}
					style={{display: "flex", width: "100%"}}
				>
					<defs>
						<filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
							<feDropShadow
								dx="0"
								dy="0.5"
								stdDeviation="0.5"
								floodColor="#000000"
								floodOpacity="0.3"
							/>
						</filter>
					</defs>
					<ZoomableGroup
						zoom={position.zoom}
						center={position.coordinates}
						onMoveEnd={handleMoveEnd}
						filterZoomEvent={handleFilter}
						maxZoom={20}
						minZoom={1}
						translateExtent={[
							[-width * 0.2, -height * 0.2],
							[width * 1.2, height * 1.2],
						]}
					>
						<Geographies
							geography={geographyPath}
							fill="#D6D6DA"
							stroke="#FFFFFF"
							strokeWidth={0.5}
						>
							{({geographies}) =>
								geographies.map((geo) => {
									const isMarked = markedCountries.includes(geo.properties.name);
									const countryColor =
										getCountryColor?.(geo.properties.name) || "#DBE4E6";

									return (
										<Geography
											key={geo.rsmKey}
											onMouseOver={() =>
												onGeographyHover?.(geo.properties.name, isMarked)
											}
											onMouseLeave={() => onGeographyLeave?.()}
											geography={geo}
											style={geographyStyle(isMarked, countryColor)}
											data-tooltip-id={geo.properties.name}
										/>
									);
								})
							}
						</Geographies>

						{/* US States Layer - always visible */}
						{
							<Geographies
								geography="/us-states.json"
								fill="#D6D6DA"
								stroke="#FFFFFF"
								strokeWidth={0.3}
							>
								{({geographies}) =>
									geographies.map((geo) => {
										const stateName = geo.properties.name;
										const region = getUSStateRegion(stateName);
										const stateColor = getUSStateColor(stateName);
										const hoverColor = getUSStateHoverColor(stateName);

										return (
											<Geography
												key={geo.rsmKey}
												geography={geo}
												onMouseOver={() =>
													onGeographyHover?.(
														`${stateName}`,
														markedCountries.includes(stateName),
													)
												}
												onMouseLeave={() => onGeographyLeave?.()}
												style={{
													default: {
														fill: stateColor,
														stroke: "#FFFFFF",
														strokeWidth: 0.3,
														outline: "none",
														transition: "all 250ms",
														cursor: region ? "pointer" : "default",
													},
													hover: {
														fill: hoverColor,
														stroke: "#FFFFFF",
														strokeWidth: 0.5,
														outline: "none",
														transition: "all 250ms",
														cursor: "pointer",
													},
													pressed: {
														outline: "none",
														transition: "all 250ms",
														cursor: "grabbing",
													},
												}}
												data-tooltip-id={stateName}
											/>
										);
									})
								}
							</Geographies>
						}

						{/* US Region Labels - always visible, gets bigger when zoomed */}
						{US_REGION_LABELS.map((region) => (
							<Marker
								key={`us-region-${region.name}`}
								coordinates={region.labelCoordinates}
							>
								<text
									textAnchor="middle"
									style={{
										// Base font size of 4px, scales up to 8px when focused on US
										fontSize: `${
											isUSFocused ? Math.min(8, 3 * position.zoom) : 4
										}px`,
										fontWeight: 600,
										fill: region.color,
										pointerEvents: "none",
										textShadow:
											"0.5px 0.5px 1px white, -0.5px -0.5px 1px white",
										letterSpacing: "0.5px",
										opacity: isUSFocused ? 1 : 0.8,
										transition: "all 300ms ease",
									}}
								>
									{region.name}
								</text>
							</Marker>
						))}

						{/* Continent Labels */}
						{position.zoom <= 1.7 &&
							Object.keys(regionsByCountry).map((region) => {
								const labels = REGION_CONFIG[region];

								if (!labels) return null;

								return labels.map((item) => (
									<Marker
										key={`region-label-${item.label}`}
										coordinates={item.coordinates}
									>
										<text
											textAnchor="middle"
											style={{
												fontSize: "10px",
												fontWeight: 500,
												fill: "#999999",
												pointerEvents: "none",
												textShadow: "1px 1px 2px white",
												textTransform: "uppercase",
												opacity: 0.6,
											}}
										>
											{item.label}
										</text>
									</Marker>
								));
							})}

						{/* Ocean Labels */}
						{position.zoom <= 1.3 &&
							OCEAN_CONFIG.map((ocean, idx) => (
								<Marker key={`ocean-label-${idx}`} coordinates={ocean.coordinates}>
									<text
										textAnchor="middle"
										style={{
											fontSize: "8px",
											fontWeight: 400,
											fill: "#aaaaaa",
											pointerEvents: "none",
											fontStyle: "italic",
											textShadow: "1px 1px 1px white",
											textTransform: "uppercase",
											opacity: 0.45,
										}}
									>
										{ocean.label}
									</text>
								</Marker>
							))}

						{/* Custom Markers / Render Prop */}
						{children?.(position)}
					</ZoomableGroup>
				</ComposableMap>
			</div>
		</div>
	);
};

export default WorldMap;
